import { useMemo, useState, type DragEvent } from "react";
import type { CmmdTaskStatus } from "@cmmd-forge/contracts";
import type { WorkItem } from "../homebase/unifiedWorkItems";
import { STATUS_CONFIGS, getDisplayStatus } from "./statusConfig";
import { TaskCard } from "./TaskCard";
import { Input } from "../ui/input";
import { Button } from "../ui/button";
import { compareBoardItemsByStatus } from "./boardSorting";

interface TaskBoardViewProps {
  items: readonly WorkItem[];
  selectedId: string | null;
  onSelect: (id: string) => void;
  onUpdateTaskStatus?: (id: string, status: CmmdTaskStatus) => void;
  onCreateTask?: (input: { title: string; status: CmmdTaskStatus }) => void;
  onDeleteTask?: (id: string) => void;
}

export function TaskBoardView({
  items,
  selectedId,
  onSelect,
  onUpdateTaskStatus,
  onCreateTask,
  onDeleteTask,
}: TaskBoardViewProps) {
  const [draftStatus, setDraftStatus] = useState<CmmdTaskStatus | null>(null);
  const [draftTitle, setDraftTitle] = useState("");
  const [dragOverStatus, setDragOverStatus] = useState<CmmdTaskStatus | null>(null);

  const itemsByStatus = useMemo(() => {
    const byStatus = new Map<CmmdTaskStatus, WorkItem[]>(
      STATUS_CONFIGS.map(({ status }) => [status, [] as WorkItem[]]),
    );
    for (const item of items) {
      const resolved = getDisplayStatus(item) as CmmdTaskStatus;
      byStatus.get(resolved)?.push(item);
    }
    for (const [status, cards] of byStatus.entries()) {
      byStatus.set(
        status,
        cards.toSorted((left, right) => compareBoardItemsByStatus(status, left, right)),
      );
    }
    return byStatus;
  }, [items]);

  const submitDraft = (status: CmmdTaskStatus) => {
    const trimmed = draftTitle.trim();
    if (!onCreateTask || trimmed.length === 0) return;
    onCreateTask({ title: trimmed, status });
    setDraftStatus(null);
    setDraftTitle("");
  };

  const handleDropInColumn = (status: CmmdTaskStatus, event: DragEvent<HTMLDivElement>) => {
    if (!onUpdateTaskStatus) return;
    event.preventDefault();
    setDragOverStatus(null);
    const itemId = event.dataTransfer.getData("text/command-center-task-id");
    if (!itemId) return;
    const droppedItem = items.find((item) => item.id === itemId);
    if (!droppedItem) return;
    if (droppedItem.source === "cmmd-task") return;
    const currentStatus = getDisplayStatus(droppedItem);
    if (currentStatus === status) return;
    onUpdateTaskStatus(itemId, status);
  };

  return (
    <div className="relative flex flex-1 gap-4 overflow-x-auto px-4 pt-3 pb-4 sm:px-5 sm:pt-4 sm:pb-5">
      {STATUS_CONFIGS.map(({ status, label, dotColor }) => {
        const cards = itemsByStatus.get(status) ?? [];
        const isDrafting = draftStatus === status;
        return (
          <div
            key={status}
            className="flex min-w-[300px] max-w-[300px] flex-col"
            onDragOver={(event) => {
              event.preventDefault();
              setDragOverStatus(status);
            }}
            onDragLeave={() =>
              setDragOverStatus((current) => (current === status ? null : current))
            }
            onDrop={(event) => handleDropInColumn(status, event)}
          >
            <div className="mb-2 flex items-center gap-2 px-0.5">
              <span className={`inline-block size-1.5 rounded-full ${dotColor}`} />
              <span className="text-sm font-semibold uppercase tracking-[0.08em] text-muted-foreground/85">
                {label}
              </span>
              <span className="ml-1 text-sm tabular-nums text-muted-foreground/60">
                {cards.length}
              </span>
              <button
                type="button"
                aria-label={`Add task to ${label}`}
                title={`Add task to ${label}`}
                onClick={() => {
                  if (!onCreateTask) return;
                  setDraftStatus(status);
                  setDraftTitle("");
                }}
                className="ml-auto inline-flex size-5 items-center justify-center rounded-md border border-border text-xs leading-none text-muted-foreground/75 transition-colors hover:bg-muted hover:text-foreground disabled:cursor-not-allowed disabled:opacity-50"
                disabled={!onCreateTask}
              >
                +
              </button>
            </div>

            {isDrafting && onCreateTask && (
              <form
                className="mb-2 rounded-lg border border-border bg-card/70 p-2"
                onSubmit={(event) => {
                  event.preventDefault();
                  submitDraft(status);
                }}
              >
                <Input
                  nativeInput
                  value={draftTitle}
                  onChange={(event) => setDraftTitle(event.currentTarget.value)}
                  placeholder="Task title"
                  autoFocus
                  className="h-8"
                />
                <div className="mt-2 flex items-center justify-end gap-1.5">
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    className="h-7 text-xs"
                    onClick={() => {
                      setDraftStatus(null);
                      setDraftTitle("");
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    size="sm"
                    className="h-7 text-xs"
                    disabled={draftTitle.trim() === ""}
                  >
                    Create
                  </Button>
                </div>
              </form>
            )}

            {cards.length === 0 && !isDrafting ? (
              <div className="flex min-h-[92px] items-center justify-center rounded-lg border border-dashed border-border/80 bg-card/40 px-3 text-center text-sm text-muted-foreground/70">
                No tasks yet
              </div>
            ) : (
              <div
                className={`flex flex-col gap-2 overflow-y-auto rounded-lg ${dragOverStatus === status ? "bg-muted/25 p-2" : ""}`}
              >
                {cards.map((item) => (
                  <TaskCard
                    key={item.id}
                    item={item}
                    isSelected={selectedId === item.id}
                    onSelect={onSelect}
                    columnStatus={status}
                    {...(onDeleteTask ? { onDeleteTask } : {})}
                    {...(onUpdateTaskStatus ? { onUpdateStatus: onUpdateTaskStatus } : {})}
                  />
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
